import React from 'react'
import { Card, Typography, Space, Button, Slider, Progress } from 'antd'
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
  SoundOutlined,
} from '@ant-design/icons'
import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import './GlobalAudioPlayer.scss'

const { Text } = Typography

export const GlobalAudioPlayer: React.FC = () => {
  const {
    currentSong,
    queue,
    currentIndex,
    isPlaying,
    currentTime,
    duration,
    volume,
    playNext,
    playPrevious,
    togglePlayPause,
    setVolume,
    seek,
  } = useAudioPlayer()

  if (!currentSong) {
    return null
  }

  const formatTime = (time: number) => {
    if (!time || isNaN(time)) return '0:00'
    const mins = Math.floor(time / 60)
    const secs = Math.floor(time % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="global-audio-player">
      <Card className="player-card" bordered={false}>
        <div className="player-content">
          <div className="song-info">
            {currentSong.imageUrl && (
              <img
                src={currentSong.imageUrl}
                alt={currentSong.title}
                className="song-cover"
              />
            )}
            <Space direction="vertical" size={0} className="song-details">
              <Text strong className="song-title" ellipsis>
                {currentSong.title}
              </Text>
              <Text type="secondary" className="song-artist" ellipsis>
                {currentSong.artist}
              </Text>
            </Space>
          </div>

          <div className="player-controls">
            <div className="control-buttons">
              <Button
                type="text"
                icon={<StepBackwardOutlined />}
                onClick={playPrevious}
                disabled={queue.length <= 1}
              />
              <Button
                type="primary"
                shape="circle"
                size="large"
                icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={togglePlayPause}
              />
              <Button
                type="text"
                icon={<StepForwardOutlined />}
                onClick={playNext}
                disabled={queue.length <= 1}
              />
            </div>

            <div className="progress-section">
              <Text className="time-text">{formatTime(currentTime)}</Text>
              <Slider
                className="progress-slider"
                value={progressPercent}
                onChange={(value) => {
                  const newTime = (value / 100) * duration
                  seek(newTime)
                }}
                tooltip={{ formatter: null }}
              />
              <Text className="time-text">{formatTime(duration)}</Text>
            </div>
          </div>

          <div className="volume-section">
            <SoundOutlined />
            <Slider
              className="volume-slider"
              value={volume * 100}
              onChange={(value) => setVolume(value / 100)}
              tooltip={{ formatter: (value) => `${value}%` }}
            />
            <Text type="secondary" className="queue-info">
              {currentIndex + 1} / {queue.length}
            </Text>
          </div>
        </div>
      </Card>
    </div>
  )
}
